import { useEffect, useRef, useState } from "react";

import { loadCompleted, MISSION_EVENT, rankFor, type MissionId } from "../game";

/**
 * The mission board: the page's playable sections, styled impossible to miss.
 *
 * Feedback-driven redesign: the first card row was visually quiet enough to scroll past.
 * This version frames the interactive sections as numbered missions on a tinted panel -
 * game-level cards with big icons, step badges, and a hover lift. Clicking one
 * smooth-scrolls to the section AND flashes it (`flash-target`), so the visitor always
 * knows where they landed; the floating BackToTop button brings them back to the board.
 *
 * Second pass (user feedback, 2026-08-15): the 판정 영수증 (DecisionTrace) was reachable
 * only by scrolling past mission 3, so it gets its own mission card; the board title now
 * says the real count. MissionDock is the compact sticky twin of this board - it follows
 * the visitor down the page so every mission stays one click away mid-scroll.
 *
 * Third pass (user feedback, 2026-08-15): the board keeps score. Completing a mission
 * for real (see game.ts - server actions only, never reading) checks the card off and
 * raises the visitor's rank, with a brief level-up banner. The score is honest about
 * where it lives: this browser only.
 */

const MISSIONS: { id: MissionId; icon: string; title: string; sub: string }[] = [
  {
    id: "try-transfer",
    icon: "🎮",
    title: "송금해 보기",
    sub: "내 송금, AI가 통과시킬까 잡을까?",
  },
  {
    id: "try-attack",
    icon: "🥊",
    title: "AI 공격하기",
    sub: "사기꾼처럼 속여서 AI를 멈춰보기",
  },
  {
    id: "try-break",
    icon: "🔨",
    title: "고장 내보기",
    sub: "부숴도 안전한지 직접 확인",
  },
  {
    id: "try-numbers",
    icon: "🤔",
    title: "숫자로 보기",
    sub: "사람이 다 하면 진짜 안 될까?",
  },
  {
    id: "try-review",
    icon: "🧑‍💼",
    title: "은행원 되어보기",
    sub: "이번엔 당신이 판정할 차례",
  },
  {
    id: "try-receipt",
    icon: "🧾",
    title: "판정 영수증 보기",
    sub: "판정이 장부에 남긴 기록 — 미션 3을 깨면 발급돼요",
  },
];

/** Breathing room between the sticky dock and the section it just scrolled to. */
const JUMP_GAP_PX = 14;

function jump(id: string) {
  const target = document.getElementById(id);
  if (!target) return;

  // scrollIntoView({block: "start"}) puts the section's top at the viewport's top - which
  // is exactly where the sticky mission dock sits, so the section's own heading landed
  // underneath it. Scroll manually to (section top - dock height) instead. The dock is
  // measured rather than assumed: it wraps to two rows on narrow screens and collapses to
  // one line when the visitor folds it.
  const dock = document.querySelector<HTMLElement>(".mission-dock");
  const offset = (dock?.offsetHeight ?? 0) + JUMP_GAP_PX;
  const top = target.getBoundingClientRect().top + window.scrollY - offset;
  if (typeof window.scrollTo === "function") {
    window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
  }

  // A brief glow on arrival so the visitor knows exactly where the jump landed.
  target.classList.remove("flash-target");
  // Force a reflow so re-clicking the same card restarts the animation.
  void target.offsetWidth;
  target.classList.add("flash-target");
  window.setTimeout(() => target.classList.remove("flash-target"), 1800);
}

/** Re-renders whenever a mission completes anywhere on the page. */
function useMissionScore(): Set<MissionId> {
  const [done, setDone] = useState<Set<MissionId>>(() => loadCompleted());
  useEffect(() => {
    const onMission = () => setDone(loadCompleted());
    window.addEventListener(MISSION_EVENT, onMission);
    return () => window.removeEventListener(MISSION_EVENT, onMission);
  }, []);
  return done;
}

export function QuickNav() {
  const done = useMissionScore();
  const rank = rankFor(done.size);
  const prevTitle = useRef(rank.title);
  const [levelUp, setLevelUp] = useState<string | null>(null);

  useEffect(() => {
    if (rank.title !== prevTitle.current) {
      prevTitle.current = rank.title;
      if (done.size > 0) {
        setLevelUp(`${rank.icon} 레벨 업! 당신은 이제 ${rank.title}`);
        const timer = window.setTimeout(() => setLevelUp(null), 4000);
        return () => window.clearTimeout(timer);
      }
    }
  }, [rank.icon, rank.title, done.size]);

  return (
    <div className="mission-board" role="navigation" aria-label="체험 바로가기">
      <p className="mission-title">
        🕹️ 오늘의 미션{" "}
        <span className="mission-title-sub">— 여섯 개, 전부 직접 해보세요!</span>
        <span className="mission-score">
          {MISSIONS.map((m) => (
            <span
              key={m.id}
              className={done.has(m.id) ? "mission-pip mission-pip--on" : "mission-pip"}
              aria-hidden="true"
            >
              {done.has(m.id) ? "🏅" : "⚪"}
            </span>
          ))}{" "}
          {done.size}/6 · {rank.icon} {rank.title}
        </span>
      </p>
      <p className="mission-howto">
        아래 카드를 하나 깰 때마다 <strong>메달 🏅 1개</strong>를 드려요. 메달을 모으면
        칭호가 올라갑니다: 🙋 방문자 → 🔍 수습 심사역 → 🕵️ FDS 심사역 → 🎖️ 수석 심사역 →{" "}
        <strong>🏆 6개 다 모으면 FDS 마스터!</strong>
      </p>
      {levelUp && <p className="rank-flash">{levelUp}</p>}
      <div className="mission-cards">
        {MISSIONS.map((m, i) => (
          <button
            key={m.id}
            className={`mission-card${done.has(m.id) ? " mission-card--done" : ""}`}
            onClick={() => jump(m.id)}
          >
            <span className="mission-step">
              {done.has(m.id) ? "✅ CLEAR" : `MISSION ${i + 1}`}
            </span>
            <span className="mission-icon">{m.icon}</span>
            <span className="mission-name">{m.title}</span>
            <span className="mission-sub">{m.sub}</span>
          </button>
        ))}
      </div>
      <p className="mission-honesty muted">
        점수는 실제로 수행한 검증만 셉니다 — 읽기만 해서는 안 올라가요. 이 브라우저에만
        저장되고 서버로는 가지 않습니다.
      </p>
    </div>
  );
}

/** Sticky one-line version of the board: follows the scroll so missions stay reachable.
 *
 * Collapsible (user feedback, 2026-08-15): on narrow screens the dock wraps to two rows
 * and eats a real slice of the viewport, so the visitor can fold it to a single line.
 * Folded or open, `jump()` measures it before scrolling - the section it jumps to never
 * lands underneath the dock. */
export function MissionDock() {
  const done = useMissionScore();
  const [open, setOpen] = useState(true);

  return (
    <nav
      className={`mission-dock${open ? "" : " mission-dock--collapsed"}`}
      aria-label="미션 바로가기 (고정)"
    >
      <button
        type="button"
        className="mission-dock-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        {open ? "▲ 미션 바 접기" : "▼ 미션 바 펼치기"}
      </button>
      <span className="mission-dock-score" title="모은 메달 / 전체 미션">
        🏅 {done.size}/6
      </span>
      {open &&
        MISSIONS.map((m, i) => (
          <button key={m.id} type="button" onClick={() => jump(m.id)} title={m.title}>
            {done.has(m.id) ? "✅" : m.icon}{" "}
            <span className="mission-dock-num">{i + 1}</span>
            <span className="mission-dock-label">{m.title}</span>
          </button>
        ))}
    </nav>
  );
}
